/**
 * Shared style constants and helpers for route map and supply flow DAG.
 * Keeps edge stroke, arrows, and colors consistent across both visualizations.
 */

import { ROUTE_PATH_COLORS } from './routeColors';

export { ROUTE_PATH_COLORS };

export const EDGE_STROKE_WIDTH = 3;
export const EDGE_STROKE_WIDTH_HOVER = 4;
export const EDGE_OFFSET_SPACING = 4;

/** Arrow at edge midpoint: triangle tip at origin, base along +x */
export const ARROW_WIDTH = 16;
export const ARROW_HEIGHT = 12;
/** Centroid of tip-at-origin triangle; offset so centroid is at (0,0) */
export const ARROW_CENTROID_OFFSET_X = (2 / 3) * ARROW_WIDTH;

/** SVG path d for arrow shape (tip at origin, base to +x). Use with translate(midX, midY) rotate(angle). */
export const ARROW_PATH_D = `M ${-ARROW_CENTROID_OFFSET_X} 0 L ${ARROW_WIDTH - ARROW_CENTROID_OFFSET_X} ${-ARROW_HEIGHT / 2} L ${ARROW_WIDTH - ARROW_CENTROID_OFFSET_X} ${ARROW_HEIGHT / 2} Z`;

export type EdgeGeometry = {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    midX: number;
    midY: number;
    angleDeg: number;
    pathD: string;
    /** Point along edge for label (e.g. 0.35 from start) to reduce overlap with nodes */
    labelX: number;
    labelY: number;
};

/**
 * Compute edge geometry, optionally shifting the line perpendicular for parallel edges.
 * @param perpendicularOffsetPx - shift in pixels (e.g. (idx - (n-1)/2) * EDGE_OFFSET_SPACING)
 */
export function getEdgeGeometry(
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    /** Fraction along edge for label (0.5 = midpoint) */
    labelT = 0.5,
    perpendicularOffsetPx = 0
): EdgeGeometry {
    let x1 = fromX;
    let y1 = fromY;
    let x2 = toX;
    let y2 = toY;
    if (perpendicularOffsetPx !== 0) {
        const dx = toX - fromX;
        const dy = toY - fromY;
        const len = Math.hypot(dx, dy) || 1;
        const perpX = (-dy / len) * perpendicularOffsetPx;
        const perpY = (dx / len) * perpendicularOffsetPx;
        x1 += perpX;
        y1 += perpY;
        x2 += perpX;
        y2 += perpY;
    }
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;
    const angleDeg = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
    /* Round line segment to integers so the stroke rasterizes reliably (avoids one edge disappearing until hover). */
    const pathD = `M ${Math.round(x1)} ${Math.round(y1)} L ${Math.round(x2)} ${Math.round(y2)}`;
    const labelX = x1 + (x2 - x1) * labelT;
    const labelY = y1 + (y2 - y1) * labelT;
    return { x1, y1, x2, y2, midX, midY, angleDeg, pathD, labelX, labelY };
}
